/**
 * LCDD Product Catalogue Page (Read-Only PWA version)
 *
 * Differences from frontend-sales/CataloguePage:
 * - Data loaded from IndexedDB (Dexie) — works offline
 * - Uses MUI Community DataGrid (not DataGridPro)
 * - No edit mode (category5, marketing description)
 * - No PUT API calls
 * - Customer pricing fetched on-demand; disabled when offline
 */

import { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Stack,
  Chip,
  Button,
  IconButton,
  CircularProgress,
  Divider,
  InputAdornment,
  FormControlLabel,
  Switch,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Drawer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  DataGrid,
  type GridColDef,
  type GridRowSelectionModel,
  type GridSortModel,
  type GridFilterModel,
  type GridColumnVisibilityModel,
} from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import PrintIcon from '@mui/icons-material/Print';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import DeselectIcon from '@mui/icons-material/Deselect';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import CloseIcon from '@mui/icons-material/Close';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useTranslation } from 'react-i18next';
import { db, loadOverridesMap, mergeOverrides, hasOverride, type ItemOverrideRow } from '../shared/services/db';
import type { ItemRow, AirlineTier } from '../shared/types';
import { AIRLINE_TIERS } from '../shared/types';
import { getAllergens, getSuitability, ALLERGEN_LABELS, SUITABILITY_LABELS, getBadgeColor } from '../utils/allergenHelpers';
import { boldAllergens } from '../utils/boldAllergens';
import { generateCatalogueHtml, compressPhotosForPdf, cleanHandlingGuideline, type SupportedLang } from '../utils/generateCatalogueHtml';
import { localeField } from '../utils/localeField';
import i18n from '../i18n';
// react-router-dom no longer needed — Lookbook opens in new tab via window.open
import { useLiveSettings } from '../hooks/useLiveSettings';
import { useSync } from '../shared/contexts/SyncContext';
import { OfflinePage } from './OfflinePage';
import { postSyncMessage } from '../shared/services/syncChannel';
import { productPhotoUrl } from '../shared/services/photoCache';

export const LOOKBOOK_CONTEXT_KEY = 'activeLookbookContext';

// Lookbook opens in a new tab, so it needs an absolute path. Derive it from the
// Vite base so it works for BOTH the standalone root deploy (base "/") and the
// embedded "/catalogue/" build. BASE_URL always ends with a trailing slash.
const LOOKBOOK_URL = `${import.meta.env.BASE_URL}lookbook`;

export type LookbookContext = {
  presentationSetIds: number[];
  showIngredients: boolean;
  airlineMode: boolean;
  showPrices: boolean;
  showDefrostLife?: boolean;
  showHandling?: boolean;
  tierEntries: [number, string][];
  timestamp: number;
};

const BASE_COLUMNS: GridColDef<ItemRow>[] = [
  { field: 'itemcode', headerName: 'Code', width: 90 },
  // Description = master data (edited in Trac-IT) → read-only here.
  { field: 'itemdescription', headerName: 'Description', flex: 1, minWidth: 200 },
  // Marketing Title / Description = personalizable (local override, per user).
  {
    field: 'item_marketingproducttitle',
    headerName: 'Marketing Title',
    flex: 1,
    minWidth: 160,
    editable: true,
  },
  {
    field: 'item_marketingdescription',
    headerName: 'Marketing Description',
    flex: 1,
    minWidth: 200,
    editable: true,
  },
  {
    field: 'category6_description',
    headerName: 'FP Category',
    width: 130,
    valueGetter: (_value: unknown, row: ItemRow) => row.category6_description ?? '',
  },
  {
    field: 'category5_description',
    headerName: 'Web Category',
    width: 130,
    valueGetter: (_value: unknown, row: ItemRow) => row.category5_description ?? '',
  },
  {
    field: 'itemislive',
    headerName: 'Live',
    width: 60,
    renderCell: params => (params.value ? '✓' : ''),
  },
];

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export const CataloguePage: React.FC = () => {
  const { t } = useTranslation();
  const settings = useLiveSettings();
  const { isOnline, lastSyncedAt, isSyncing, photoCacheBust } = useSync();

  const [items, setItems] = useState<ItemRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [liveOnly, setLiveOnly] = useState(true);
  // Ordered array of selected itemids — source of truth for basket order.
  // Persisted to localStorage so selections survive navigation (cleared on logout / manual deselect).
  const [selectedOrder, setSelectedOrder] = useState<number[]>(() => {
    try {
      const s = localStorage.getItem('catalogue-selected-order');
      return s ? (JSON.parse(s) as number[]) : [];
    } catch { return []; }
  });
  // Derived Set for O(1) lookups (DataGrid, filters)
  const selectedIds = useMemo(() => new Set(selectedOrder), [selectedOrder]);

  // Persist basket to localStorage on every change
  useEffect(() => {
    localStorage.setItem('catalogue-selected-order', JSON.stringify(selectedOrder));
  }, [selectedOrder]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [filterToSelected, setFilterToSelected] = useState(false);
  const [pdfConfirmOpen, setPdfConfirmOpen] = useState(false);

  const [previewItem, setPreviewItem] = useState<ItemRow | null>(null);
  const [photoHidden, setPhotoHidden] = useState(false);

  // Show/hide catalogue price column (item_webprice)
  const [showPrices, setShowPrices] = useState(false);

  // Show/hide ingredients in grid and detail
  const [showIngredients, setShowIngredients] = useState(true);

  // Show/hide defrosted (secondary) shelf life + handling guideline on PDF / Lookbook
  const [showDefrostLife, setShowDefrostLife] = useState(false);
  const [showHandling, setShowHandling] = useState(false);

  // Local per-user personalization overrides (marketing title/desc/price).
  const [overridesMap, setOverridesMap] = useState<Map<number, ItemOverrideRow>>(new Map());
  const [editingMarketingDesc, setEditingMarketingDesc] = useState<{ itemid: number; value: string } | null>(null);
  const [savingMarketingDesc, setSavingMarketingDesc] = useState(false);

  // Airline Mode: tiering (Business / First / Economy)
  const [airlineMode, setAirlineMode] = useState(() => localStorage.getItem('catalogue-airline-mode') === 'true');
  const [tierMap, setTierMap] = useState<Map<number, AirlineTier>>(new Map());

  // Suitability & allergen filters — both use 3-state cycle: off → include → exclude → off
  const [allergenFilter, setAllergenFilter] = useState<Map<string, 'include' | 'exclude'>>(new Map());
  const [suitabilityFilter, setSuitabilityFilter] = useState<Map<string, 'include' | 'exclude'>>(new Map());

  // DataGrid state persistence
  const GRID_STATE_KEY = 'catalogue-grid-state';
  const defaultVisibility: GridColumnVisibilityModel = { item_ingredientlist: false, item_marketingdescription: false };

  const [sortModel, setSortModel] = useState<GridSortModel>(() => {
    try { const s = localStorage.getItem(GRID_STATE_KEY + ':sort'); return s ? JSON.parse(s) : []; } catch { return []; }
  });
  const [filterModel, setFilterModel] = useState<GridFilterModel>(() => {
    try { const s = localStorage.getItem(GRID_STATE_KEY + ':filter'); return s ? JSON.parse(s) : { items: [] }; } catch { return { items: [] }; }
  });
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(() => {
    try { const s = localStorage.getItem(GRID_STATE_KEY + ':visibility'); return s ? JSON.parse(s) : defaultVisibility; } catch { return defaultVisibility; }
  });

  /* Reset editing state when preview item changes */
  useEffect(() => {
    setEditingMarketingDesc(null);
  }, [previewItem?.itemid]);

  const broadcastSettings = useCallback((
    overrides: Partial<{ airlineMode: boolean; showIngredients: boolean; showPrices: boolean; showDefrostLife: boolean; showHandling: boolean; tierEntries: [number, string][] }>
  ) => {
    postSyncMessage({
      type: 'SETTINGS_CHANGE',
      airlineMode: overrides.airlineMode ?? airlineMode,
      showIngredients: overrides.showIngredients ?? showIngredients,
      showPrices: overrides.showPrices ?? showPrices,
      showDefrostLife: overrides.showDefrostLife ?? showDefrostLife,
      showHandling: overrides.showHandling ?? showHandling,
      tierEntries: (overrides.tierEntries ?? [...tierMap.entries()]) as [number, import('../shared/types').AirlineTier][],
    });
  }, [airlineMode, showIngredients, showPrices, showDefrostLife, showHandling, tierMap]);

  const persistAirlineMode = useCallback((on: boolean) => {
    setAirlineMode(on);
    localStorage.setItem('catalogue-airline-mode', on ? 'true' : 'false');
    broadcastSettings({ airlineMode: on });
  }, [broadcastSettings]);

  const cycleAllergen = useCallback((label: string) => {
    setAllergenFilter(prev => {
      const next = new Map(prev);
      const current = next.get(label);
      if (!current) next.set(label, 'include');
      else if (current === 'include') next.set(label, 'exclude');
      else next.delete(label);
      return next;
    });
  }, []);

  const cycleSuitability = useCallback((label: string) => {
    setSuitabilityFilter(prev => {
      const next = new Map(prev);
      const current = next.get(label);
      if (!current) next.set(label, 'include');
      else if (current === 'include') next.set(label, 'exclude');
      else next.delete(label);
      return next;
    });
  }, []);

  /* ---- Load item tiers from IndexedDB ---- */
  useEffect(() => {
    db.itemTiers.toArray().then(rows => {
      const map = new Map<number, AirlineTier>();
      for (const r of rows) map.set(r.itemid, r.tier);
      setTierMap(map);
    });
  }, [lastSyncedAt]);

  /* ---- Load local personalization overrides from IndexedDB ---- */
  useEffect(() => {
    loadOverridesMap().then(setOverridesMap);
  }, [lastSyncedAt]);

  /* ---- Load from IndexedDB ---- */
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const dbItems = await db.items.toArray();
        setItems(dbItems);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [lastSyncedAt]); // reload when sync completes

  /* ---- FP items (category = 5 = Finished Products), with local personalization merged ---- */
  // Overrides are merged here so EVERYTHING downstream (grid, drawer, PDF, saved
  // portfolios) reflects the user's personalization without touching the master.
  const fpItems = useMemo(
    () => mergeOverrides(items.filter(i => i.itemcategory === '5'), overridesMap),
    [items, overridesMap]
  );

  /* ---- Columns: optional ingredients + optional tier ---- */
  const columns = useMemo((): GridColDef<ItemRow>[] => {
    let cols = [...BASE_COLUMNS];
    if (airlineMode) {
      cols.push({
        field: 'itemid',
        headerName: 'Tier',
        width: 140,
        renderCell: params => {
          const itemid = params.row.itemid;
          const tier = tierMap.get(itemid);
          return (
            <FormControl size="small" fullWidth sx={{ minWidth: 100 }}>
              <Select
                value={tier ?? ''}
                displayEmpty
                onChange={e => {
                  const v = e.target.value as AirlineTier | '';
                  if (!v) {
                    db.itemTiers.delete(itemid).then(() => {
                      setTierMap(prev => {
                        const next = new Map(prev);
                        next.delete(itemid);
                        return next;
                      });
                    });
                  } else {
                    db.itemTiers.put({ itemid, tier: v }).then(() => {
                      setTierMap(prev => new Map(prev).set(itemid, v));
                    });
                  }
                }}
                onClick={e => e.stopPropagation()}
                sx={{ height: 28, fontSize: '0.75rem' }}
              >
                <MenuItem value=""><em>—</em></MenuItem>
                {AIRLINE_TIERS.map(t => (
                  <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        },
      });
    }
    cols.push({
      field: 'suitability',
      headerName: 'Suitable for',
      width: 160,
      sortable: false,
      valueGetter: (_value: unknown, row: ItemRow) => getSuitability(row),
      renderCell: params => (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="center" sx={{ py: 0.25 }}>
          {((params.value as string[] | undefined) ?? []).map(s => (
            <Chip key={s} label={s} size="small" sx={{ height: 20, fontSize: 11, bgcolor: getBadgeColor(s), color: '#fff' }} />
          ))}
        </Stack>
      ),
    });
    cols.push({
      field: 'allergens',
      headerName: 'Allergens',
      width: 200,
      sortable: false,
      valueGetter: (_value: unknown, row: ItemRow) => getAllergens(row),
      renderCell: params => (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="center" sx={{ py: 0.25 }}>
          {((params.value as string[] | undefined) ?? []).map(a => (
            <Chip key={a} label={a} size="small" sx={{ height: 20, fontSize: 11, bgcolor: getBadgeColor(a), color: '#fff' }} />
          ))}
        </Stack>
      ),
    });
    if (showPrices) {
      cols.push({
        field: 'item_webprice',
        headerName: 'Catalogue Price',
        width: 120,
        editable: true,
        renderCell: (params: { value?: number | null }) =>
          params.value != null ? `£${Number(params.value).toFixed(2)}` : '—',
        valueSetter: (value: unknown, row: ItemRow) => {
          const str = String(value ?? '').trim();
          if (str === '') return { ...row, item_webprice: null };
          const parsed = parseFloat(str);
          return { ...row, item_webprice: isNaN(parsed) ? row.item_webprice : parsed };
        },
        valueFormatter: (value: number | null | undefined) =>
          value != null ? Number(value).toFixed(2) : '',
      });
    }
    cols.push({
      field: 'item_ingredientlist',
      headerName: 'Ingredients',
      flex: 1,
      minWidth: 180,
      renderCell: (params: { value?: string | null }) =>
        params.value ? (
          <Typography variant="body2" sx={{ fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 280 }}>
            {params.value}
          </Typography>
        ) : (
          '—'
        ),
    });
    return cols;
  }, [showPrices, airlineMode, tierMap]);

  /* ---- Filtered rows (search, live, suitability, allergens, filterToSelected) ---- */
  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();

    // DEBUG: log raw field values for the first FP item whenever any filter is active
    if ((suitabilityFilter.size > 0 || allergenFilter.size > 0) && fpItems.length > 0) {
      const sample = fpItems[0];
      console.log('[Filter debug] sample item:', sample.itemcode, {
        item_is_halal:      sample.item_is_halal,
        item_is_vegetarian: sample.item_is_vegetarian,
        item_is_vegan:      sample.item_is_vegan,
        item_is_gluten:     sample.item_is_gluten,
        item_is_dairy:      sample.item_is_dairy,
        item_is_egg:        sample.item_is_egg,
        item_is_nuts:       sample.item_is_nuts,
        item_is_soya:       sample.item_is_soya,
      });
      console.log('[Filter debug] getSuitability →', getSuitability(sample));
      console.log('[Filter debug] getAllergens    →', getAllergens(sample));
      console.log('[Filter debug] suitabilityFilter →', Object.fromEntries(suitabilityFilter));
      console.log('[Filter debug] allergenFilter  →', Object.fromEntries(allergenFilter));
    }

    return fpItems.filter(item => {
      if (filterToSelected && !selectedIds.has(item.itemid)) return false;
      if (liveOnly && !item.itemislive) return false;
      if (q) {
        const haystack = [
          item.itemcode,
          item.itemdescription,
          item.item_marketingproducttitle,
          item.category5_description,
          item.item_webtopcategory,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      const itemSuitability = getSuitability(item);
      for (const [label, mode] of suitabilityFilter) {
        const has = itemSuitability.includes(label);
        if (mode === 'include' && !has) return false;
        if (mode === 'exclude' && has) return false;
      }
      const itemAllergens = getAllergens(item);
      for (const [label, mode] of allergenFilter) {
        const has = itemAllergens.includes(label);
        if (mode === 'include' && !has) return false;
        if (mode === 'exclude' && has) return false;
      }
      return true;
    });
  }, [fpItems, search, liveOnly, suitabilityFilter, allergenFilter, filterToSelected, selectedIds]);

  /* ---- Selection (persistent across search/filter, order-preserving) ---- */
  const handleSelectionChange = useCallback(
    (model: GridRowSelectionModel) => {
      if (!model || typeof model !== 'object' || !('ids' in model)) return;
      const gridIds = new Set(model.ids as Iterable<number>);
      setSelectedOrder(prev => {
        const visibleIds = new Set(filteredItems.map(i => i.itemid));
        // Keep items currently outside the filtered view (other pages, other searches)
        const keptFromHidden = prev.filter(id => !visibleIds.has(id));
        // Keep already-selected visible items in their existing order
        const existingVisible = prev.filter(id => visibleIds.has(id) && gridIds.has(id));
        // Append newly checked items at the end
        const prevSet = new Set(prev);
        const newlyAdded = [...gridIds].filter(id => !prevSet.has(id));
        const next = [...keptFromHidden, ...existingVisible, ...newlyAdded];
        postSyncMessage({ type: 'SELECTION_CHANGE', selectedIds: next });
        return next;
      });
    },
    [filteredItems]
  );

  const selectionModel = useMemo(
    () => ({ type: 'include' as const, ids: selectedIds }),
    [selectedIds]
  );

  /* ---- Basket helpers ---- */
  const handleRemoveFromSelection = useCallback((id: number) => {
    setSelectedOrder(prev => prev.filter(x => x !== id));
  }, []);

  const handleReorder = useCallback((id: number, dir: 'up' | 'down') => {
    setSelectedOrder(prev => {
      const idx = prev.indexOf(id);
      if (idx === -1) return prev;
      const next = [...prev];
      const swapIdx = dir === 'up' ? idx - 1 : idx + 1;
      if (swapIdx < 0 || swapIdx >= next.length) return prev;
      [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
      return next;
    });
  }, []);

  // Full ItemRow objects in basket order (for drawer list and PDF)
  const selectedItems = useMemo(() => {
    const map = new Map(fpItems.map(i => [i.itemid, i]));
    return selectedOrder.map(id => map.get(id)).filter(Boolean) as ItemRow[];
  }, [selectedOrder, fpItems]);

  /* ---- Local personalization (per-user overrides, never sent to the master) ---- */
  // Upsert a partial override for one item into IndexedDB + local state. These are
  // the user's own catalogue/portfolio tweaks; the master record stays in Trac-IT.
  const applyOverrideEdit = useCallback(async (itemid: number, patch: Partial<ItemOverrideRow>) => {
    const existing = (await db.itemOverrides.get(itemid)) ?? { itemid };
    const next: ItemOverrideRow = { ...existing, ...patch, itemid };
    await db.itemOverrides.put(next);
    setOverridesMap(prev => new Map(prev).set(itemid, next));
    if (previewItem?.itemid === itemid) {
      setPreviewItem(prev => (prev ? { ...prev, ...patch } : prev));
    }
    postSyncMessage({ type: 'PRODUCT_UPDATE', itemid, patch });
  }, [previewItem]);

  // Clear ALL personalizations for one item (revert to the master/synced values).
  const handleResetOverride = useCallback(async (itemid: number) => {
    const master = items.find(i => i.itemid === itemid);
    await db.itemOverrides.delete(itemid);
    setOverridesMap(prev => {
      const next = new Map(prev);
      next.delete(itemid);
      return next;
    });
    if (previewItem?.itemid === itemid && master) {
      setPreviewItem(prev => (prev ? {
        ...prev,
        item_marketingproducttitle: master.item_marketingproducttitle,
        item_marketingdescription: master.item_marketingdescription,
        item_webprice: master.item_webprice,
      } : prev));
    }
  }, [items, previewItem]);

  const handleSaveMarketingDesc = useCallback(async () => {
    if (!editingMarketingDesc || !previewItem || previewItem.itemid !== editingMarketingDesc.itemid) return;
    setSavingMarketingDesc(true);
    try {
      await applyOverrideEdit(editingMarketingDesc.itemid, { item_marketingdescription: editingMarketingDesc.value });
      setEditingMarketingDesc(null);
    } finally {
      setSavingMarketingDesc(false);
    }
  }, [editingMarketingDesc, previewItem, applyOverrideEdit]);

  /* ---- Inline grid editing — personalization only (marketing title, marketing desc, price) ---- */
  const processRowUpdate = useCallback(
    async (newRow: ItemRow, oldRow: ItemRow): Promise<ItemRow> => {
      const patch: Partial<ItemOverrideRow> = {};

      if (newRow.item_marketingproducttitle !== oldRow.item_marketingproducttitle) {
        patch.item_marketingproducttitle = newRow.item_marketingproducttitle ?? null;
      }
      if (newRow.item_marketingdescription !== oldRow.item_marketingdescription) {
        patch.item_marketingdescription = newRow.item_marketingdescription ?? null;
      }

      const newPrice = newRow.item_webprice != null ? parseFloat(String(newRow.item_webprice)) : null;
      const oldPrice = oldRow.item_webprice != null ? parseFloat(String(oldRow.item_webprice)) : null;
      const normalizedNew = newPrice != null && !isNaN(newPrice) ? newPrice : null;
      const normalizedOld = oldPrice != null && !isNaN(oldPrice) ? oldPrice : null;
      if (normalizedNew !== normalizedOld) {
        patch.item_webprice = normalizedNew;
        newRow = { ...newRow, item_webprice: normalizedNew };
      }

      if (Object.keys(patch).length === 0) return oldRow;

      await applyOverrideEdit(newRow.itemid, patch);
      return newRow;
    },
    [applyOverrideEdit]
  );

  const handleProcessRowUpdateError = useCallback((error: unknown) => {
    console.error('Row update failed:', error);
  }, []);

  const handleSortModelChange = useCallback((model: GridSortModel) => {
    setSortModel(model);
    localStorage.setItem(GRID_STATE_KEY + ':sort', JSON.stringify(model));
  }, []);

  const handleFilterModelChange = useCallback((model: GridFilterModel) => {
    setFilterModel(model);
    localStorage.setItem(GRID_STATE_KEY + ':filter', JSON.stringify(model));
  }, []);

  const handleColumnVisibilityChange = useCallback((model: GridColumnVisibilityModel) => {
    setColumnVisibilityModel(model);
    localStorage.setItem(GRID_STATE_KEY + ':visibility', JSON.stringify(model));
  }, []);

  /* ---- PDF generation ---- */
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [pdfProgress, setPdfProgress] = useState('');

  const handleGeneratePdf = async () => {
    // Use selectedItems (ordered array) if anything is selected, else fall back to filteredItems
    const itemsToExport = selectedItems.length > 0 ? selectedItems : filteredItems;

    const activeTierMap = airlineMode ? tierMap : undefined;

    setPdfGenerating(true);
    setPdfProgress('Preparing banner…');

    let bannerDataUrl: string | null = null;
    const bannerRow = await db.assetBlobs.get('banner');
    if (bannerRow?.blob) {
      bannerDataUrl = await new Promise<string>((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result as string);
        r.onerror = () => reject(new Error('Failed to read banner'));
        r.readAsDataURL(bannerRow.blob);
      });
    }

    setPdfProgress('Compressing photos…');
    const photoMap = await compressPhotosForPdf(itemsToExport, (done, total) => {
      setPdfProgress(`Compressing photos… ${done}/${total}`);
    }, photoCacheBust);

    const lang = (i18n.language as SupportedLang) ?? 'en';

    // Remap translated fields so the PDF generator sees localised text.
    const localisedItems = lang === 'en' ? itemsToExport : itemsToExport.map(item => ({
      ...item,
      itemdescription: localeField(item, 'itemdescription', lang) ?? item.itemdescription,
      item_ingredientlist: localeField(item, 'item_ingredientlist', lang) ?? item.item_ingredientlist,
      item_handling_guideline: localeField(item, 'item_handling_guideline', lang) ?? item.item_handling_guideline,
      item_marketingproducttitle: localeField(item, 'item_marketingproducttitle', lang) ?? item.item_marketingproducttitle,
      item_marketingdescription: localeField(item, 'item_marketingdescription', lang) ?? item.item_marketingdescription,
    }));

    setPdfProgress('Building PDF…');
    const html = generateCatalogueHtml(
      localisedItems,
      settings,
      showPrices,
      activeTierMap,
      showIngredients,
      bannerDataUrl,
      photoMap,
      photoCacheBust,
      showDefrostLife,
      showHandling,
      lang,
    );
    const win = window.open('', '_blank');
    if (win) {
      win.document.open();
      win.document.write(html);
      win.document.close();
    }
    setPdfGenerating(false);
    setPdfProgress('');
  };

  /* ---- No data state ---- */
  if (!loading && items.length === 0 && !lastSyncedAt) {
    return <OfflinePage />;
  }

  const selectedCount = selectedOrder.length;
  const exportCount = selectedCount > 0 ? selectedCount : filteredItems.length;

  return (
    <Box sx={{ display: 'flex', height: '100%', gap: 0 }}>
      {/* ── Left panel: grid + toolbar ── */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Toolbar */}
        <Paper
          square
          elevation={0}
          sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
            <TextField
              data-tour="search"
              size="small"
              placeholder={t('catalogue.search_placeholder')}
              value={search}
              onChange={e => setSearch(e.target.value)}
              sx={{ flex: 1, minWidth: 220 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            <FormControlLabel
              data-tour="live-only"
              control={<Switch checked={liveOnly} onChange={e => setLiveOnly(e.target.checked)} size="small" />}
              label={<Typography variant="body2">{t('catalogue.live_only')}</Typography>}
              sx={{ mr: 0 }}
            />

            <Tooltip title={t('catalogue.select_all')}>
              <IconButton size="small" onClick={() =>
                setSelectedOrder(prev => {
                  const prevSet = new Set(prev);
                  const toAdd = filteredItems.map(i => i.itemid).filter(id => !prevSet.has(id));
                  return [...prev, ...toAdd];
                })
              }>
                <SelectAllIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('catalogue.deselect_all')}>
              <IconButton size="small" onClick={() => { setSelectedOrder([]); setFilterToSelected(false); }}>
                <DeselectIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {/* Basket counter chip — opens drawer */}
            <Chip
              data-tour="selection"
              label={selectedCount > 0 ? t('catalogue.n_selected', { count: selectedCount }) : t('catalogue.none_selected')}
              size="small"
              clickable={selectedCount > 0}
              onClick={selectedCount > 0 ? () => setDrawerOpen(true) : undefined}
              sx={{
                fontWeight: 600,
                bgcolor: selectedCount > 0 ? '#A55DA0' : '#e0e0e0',
                color: selectedCount > 0 ? '#fff' : '#888',
                cursor: selectedCount > 0 ? 'pointer' : 'default',
                '&:hover': selectedCount > 0 ? { bgcolor: '#8d4a88' } : {},
              }}
            />

            <Button
              data-tour="pdf"
              variant="contained"
              startIcon={pdfGenerating ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <PrintIcon />}
              onClick={() => setPdfConfirmOpen(true)}
              disabled={filteredItems.length === 0 || pdfGenerating}
              size="small"
              sx={{ bgcolor: '#A55DA0', '&:hover': { bgcolor: '#8d4a88' }, whiteSpace: 'nowrap' }}
            >
              {pdfGenerating ? pdfProgress || t('catalogue.generating') : t('catalogue.pdf_button', { count: exportCount })}
            </Button>
            <Tooltip title={t('catalogue.lookbook_button', { count: selectedOrder.length })}>
              <span data-tour="open-lookbook">
                <Button
                  variant="outlined"
                  startIcon={<MenuBookIcon />}
                  onClick={() => {
                    const ctx: LookbookContext = {
                      presentationSetIds: [...selectedOrder],
                      showIngredients,
                      airlineMode,
                      showPrices,
                      showDefrostLife,
                      showHandling,
                      tierEntries: airlineMode ? [...tierMap.entries()] : [],
                      timestamp: Date.now(),
                    };
                    localStorage.setItem(LOOKBOOK_CONTEXT_KEY, JSON.stringify(ctx));
                    window.open(LOOKBOOK_URL, '_blank');
                  }}
                  disabled={selectedOrder.length === 0}
                  size="small"
                  sx={{ whiteSpace: 'nowrap', borderColor: '#A55DA0', color: '#A55DA0' }}
                >
                  {t('catalogue.lookbook_button', { count: selectedOrder.length })}
                </Button>
              </span>
            </Tooltip>

            {!isOnline || isSyncing ? null : (
              <Typography variant="caption" sx={{ color: '#aaa', ml: 'auto' }}>
                  {t('catalogue.n_products', { count: filteredItems.length })}
                </Typography>
            )}
          </Stack>

          {/* Suitability & allergen filters (Sales-style) */}
          <Stack data-tour="filters" direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mt: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
              {t('catalogue.suitability_label')}
            </Typography>
            {SUITABILITY_LABELS.map(s => {
              const mode = suitabilityFilter.get(s.label);
              const displayLabel = t(`suitability.${s.label}`);
              return (
                <Chip
                  key={s.label}
                  label={mode === 'exclude' ? `${t('catalogue.no_prefix')}${displayLabel}` : displayLabel}
                  size="small"
                  clickable
                  onClick={() => cycleSuitability(s.label)}
                  sx={{
                    height: 22,
                    fontSize: 11,
                    bgcolor: mode === 'include' ? s.color : mode === 'exclude' ? '#b71c1c' : 'transparent',
                    color: mode ? '#fff' : 'text.secondary',
                    border: `1px solid ${mode === 'include' ? s.color : mode === 'exclude' ? '#b71c1c' : '#ccc'}`,
                    fontWeight: mode ? 700 : 400,
                    textDecoration: mode === 'exclude' ? 'line-through' : 'none',
                  }}
                />
              );
            })}
            {suitabilityFilter.size > 0 && (
              <Chip label={t('catalogue.clear')} size="small" variant="outlined" onClick={() => setSuitabilityFilter(new Map())} sx={{ height: 22, fontSize: 11 }} />
            )}
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" sx={{ mt: 0.75 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
              {t('catalogue.allergens_label')}
            </Typography>
            {ALLERGEN_LABELS.map(a => {
              const mode = allergenFilter.get(a.label);
              const displayLabel = t(`allergen.${a.label}`);
              return (
                <Chip
                  key={a.label}
                  label={mode === 'exclude' ? `${t('catalogue.no_prefix')}${displayLabel}` : displayLabel}
                  size="small"
                  clickable
                  onClick={() => cycleAllergen(a.label)}
                  sx={{
                    height: 22,
                    fontSize: 11,
                    bgcolor: mode === 'include' ? a.color : mode === 'exclude' ? '#b71c1c' : 'transparent',
                    color: mode ? '#fff' : 'text.secondary',
                    border: `1px solid ${mode === 'include' ? a.color : mode === 'exclude' ? '#b71c1c' : '#ccc'}`,
                    fontWeight: mode ? 700 : 400,
                    textDecoration: mode === 'exclude' ? 'line-through' : 'none',
                  }}
                />
              );
            })}
            {allergenFilter.size > 0 && (
              <Chip label={t('catalogue.clear_all')} size="small" variant="outlined" onClick={() => setAllergenFilter(new Map())} sx={{ height: 22, fontSize: 11 }} />
            )}
          </Stack>

          {/* Pricing row */}
          <Stack data-tour="display-toggles" direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1 }}>
            <FormControlLabel
              control={<Switch checked={airlineMode} onChange={e => persistAirlineMode(e.target.checked)} size="small" />}
              label={<Typography variant="body2">{t('catalogue.airline_mode')}</Typography>}
              sx={{ mr: 0 }}
            />
            <FormControlLabel
              control={<Switch checked={showIngredients} onChange={e => {
                setShowIngredients(e.target.checked);
                broadcastSettings({ showIngredients: e.target.checked });
              }} size="small" />}
              label={<Typography variant="body2">{t('catalogue.show_ingredients')}</Typography>}
              sx={{ mr: 0 }}
            />
            <FormControlLabel
              control={<Switch checked={showPrices} onChange={e => {
                setShowPrices(e.target.checked);
                broadcastSettings({ showPrices: e.target.checked });
              }} size="small" />}
              label={<Typography variant="body2">{t('catalogue.show_prices')}</Typography>}
              sx={{ mr: 0 }}
            />
            <FormControlLabel
              control={<Switch checked={showDefrostLife} onChange={e => {
                setShowDefrostLife(e.target.checked);
                broadcastSettings({ showDefrostLife: e.target.checked });
              }} size="small" />}
              label={<Typography variant="body2">{t('catalogue.show_defrost_life')}</Typography>}
              sx={{ mr: 0 }}
            />
            <FormControlLabel
              control={<Switch checked={showHandling} onChange={e => {
                setShowHandling(e.target.checked);
                broadcastSettings({ showHandling: e.target.checked });
              }} size="small" />}
              label={<Typography variant="body2">{t('catalogue.show_handling')}</Typography>}
              sx={{ mr: 0 }}
            />
          </Stack>
        </Paper>

        {/* DataGrid */}
        <Box data-tour="grid" sx={{ flex: 1, minHeight: 0 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <DataGrid
              rows={filteredItems}
              columns={columns}
              getRowId={row => row.itemid}
              rowSelectionModel={selectionModel}
              onRowSelectionModelChange={handleSelectionChange}
              processRowUpdate={processRowUpdate}
              onProcessRowUpdateError={handleProcessRowUpdateError}
              sortModel={sortModel}
              onSortModelChange={handleSortModelChange}
              filterModel={filterModel}
              onFilterModelChange={handleFilterModelChange}
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={handleColumnVisibilityChange}
              checkboxSelection
              disableRowSelectionOnClick
              onRowClick={params => { setPreviewItem(params.row as ItemRow); setPhotoHidden(false); }}
              density="compact"
              pageSizeOptions={[50, 100, 200]}
              initialState={{
                pagination: { paginationModel: { pageSize: 100 } },
              }}
              sx={{
                border: 0,
                '& .MuiDataGrid-row:hover': { bgcolor: '#F9E5EF' },
                '& .MuiDataGrid-row.Mui-selected': { bgcolor: '#EDE5ED' },
                '& .MuiDataGrid-columnHeaders': { bgcolor: '#363D4C', color: '#fff' },
                '& .MuiDataGrid-columnHeader': { bgcolor: '#363D4C' },
                '& .MuiDataGrid-sortIcon': { color: '#fff' },
                '& .MuiDataGrid-menuIconButton': { color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } },
                '& .MuiDataGrid-columnHeader .MuiIconButton-root': { color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } },
                '& .MuiCheckbox-root.Mui-checked': { color: '#A55DA0' },
              }}
            />
          )}
        </Box>
      </Box>

      {/* ── Selected Products Drawer ── */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: 380, display: 'flex', flexDirection: 'column' } }}
      >
        {/* Drawer header */}
        <Box sx={{ px: 2, py: 1.5, bgcolor: '#363D4C', display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          <Typography variant="subtitle2" sx={{ color: '#fff', flex: 1, fontWeight: 700 }}>
            {selectedCount > 0
              ? (selectedCount === 1 ? t('drawer.n_selected_one', { count: 1 }) : t('drawer.n_selected_other', { count: selectedCount }))
              : t('drawer.no_selected')}
          </Typography>
          <IconButton size="small" onClick={() => setDrawerOpen(false)} sx={{ color: '#fff', p: 0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Drawer sub-header actions */}
        <Box sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          <Button
            size="small"
            variant="outlined"
            color="error"
            disabled={selectedCount === 0}
            onClick={() => { setSelectedOrder([]); setFilterToSelected(false); }}
            sx={{ fontSize: '0.75rem' }}
          >
            {t('drawer.remove_all')}
          </Button>
          <Box sx={{ flex: 1 }} />
          <Tooltip title={filterToSelected ? t('catalogue.show_all_products') : t('catalogue.show_only_selected')}>
            <Chip
              icon={<FilterListIcon sx={{ fontSize: '14px !important' }} />}
              label={t('catalogue.filter_to_selected')}
              size="small"
              clickable
              onClick={() => setFilterToSelected(v => !v)}
              sx={{
                fontWeight: 600,
                fontSize: '0.72rem',
                bgcolor: filterToSelected ? '#A55DA0' : 'transparent',
                color: filterToSelected ? '#fff' : '#A55DA0',
                border: '1px solid #A55DA0',
                '& .MuiChip-icon': { color: filterToSelected ? '#fff' : '#A55DA0' },
              }}
            />
          </Tooltip>
        </Box>

        {/* Scrollable product list */}
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {selectedCount === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {t('drawer.empty_hint')}
              </Typography>
            </Box>
          ) : (
            <List dense disablePadding>
              {selectedItems.map((item, idx) => (
                <ListItem
                  key={item.itemid}
                  divider
                  sx={{ pr: 1, alignItems: 'flex-start', py: 0.75 }}
                  secondaryAction={
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0, ml: 0.5 }}>
                      <Tooltip title={t('drawer.move_up')}>
                        <span>
                          <IconButton size="small" disabled={idx === 0} onClick={() => handleReorder(item.itemid, 'up')} sx={{ p: 0.25 }}>
                            <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={t('drawer.move_down')}>
                        <span>
                          <IconButton size="small" disabled={idx === selectedItems.length - 1} onClick={() => handleReorder(item.itemid, 'down')} sx={{ p: 0.25 }}>
                            <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={t('drawer.remove')}>
                        <IconButton size="small" onClick={() => handleRemoveFromSelection(item.itemid)} sx={{ p: 0.25, color: '#c62828' }}>
                          <CloseIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  }
                >
                  <ListItemText
                    sx={{ pr: 5 }}
                    primary={
                      <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.8rem', lineHeight: 1.3 }}>
                        {item.itemcode}
                      </Typography>
                    }
                    secondary={
                      <Box component="span" sx={{ display: 'block' }}>
                        <Typography component="span" variant="body2" sx={{ fontSize: '0.78rem', color: '#444', display: 'block', lineHeight: 1.3 }}>
                          {item.itemdescription ?? '—'}
                        </Typography>
                        {item.category6_description && (
                          <Typography component="span" variant="caption" sx={{ fontSize: '0.7rem', color: '#888' }}>
                            {item.category6_description}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>

        {/* Drawer footer */}
        <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider', display: 'flex', gap: 1, flexShrink: 0 }}>
          <Button
            fullWidth
            variant="contained"
            size="small"
            startIcon={pdfGenerating ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <PrintIcon />}
            disabled={selectedCount === 0 || pdfGenerating}
            onClick={() => { setDrawerOpen(false); setPdfConfirmOpen(true); }}
            sx={{ bgcolor: '#A55DA0', '&:hover': { bgcolor: '#8d4a88' }, fontSize: '0.8rem' }}
          >
            {pdfGenerating ? (pdfProgress || t('catalogue.generating')) : t('drawer.generate_pdf', { count: selectedCount })}
          </Button>
          <Button
            fullWidth
            variant="outlined"
            size="small"
            startIcon={<MenuBookIcon />}
            disabled={selectedCount === 0}
            onClick={() => {
              const ctx: LookbookContext = {
                presentationSetIds: [...selectedOrder],
                showIngredients,
                airlineMode,
                showPrices,
                showDefrostLife,
                showHandling,
                tierEntries: airlineMode ? [...tierMap.entries()] : [],
                timestamp: Date.now(),
              };
              localStorage.setItem(LOOKBOOK_CONTEXT_KEY, JSON.stringify(ctx));
              setDrawerOpen(false);
              window.open(LOOKBOOK_URL, '_blank');
            }}
            sx={{ borderColor: '#A55DA0', color: '#A55DA0', fontSize: '0.8rem' }}
          >
            {t('drawer.lookbook')}
          </Button>
        </Box>
      </Drawer>

      {/* ── Right panel: product preview ── */}
      {previewItem && (
        <Paper
          square
          elevation={0}
          sx={{
            width: 340,
            flexShrink: 0,
            borderLeft: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Close + header */}
          <Box
            sx={{
              px: 2, py: 1.5,
              bgcolor: '#363D4C',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <VisibilityIcon sx={{ color: '#fff', fontSize: 18 }} />
            <Typography variant="subtitle2" sx={{ color: '#fff', flex: 1, fontSize: '0.85rem' }}>
              {previewItem.itemcode} — {previewItem.itemdescription}
            </Typography>
            <IconButton size="small" onClick={() => setPreviewItem(null)} sx={{ color: '#fff', p: 0.25 }}>
              ✕
            </IconButton>
          </Box>

          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            {/* Description — master data (edited in Trac-IT), read-only here */}
            <Stack direction="row" alignItems="center" gap={0.5} sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ flex: 1, color: '#333' }}>
                {localeField(previewItem, 'itemdescription', i18n.language) ?? '—'}
              </Typography>
            </Stack>

            {/* Personalization indicator + reset (this item has local edits) */}
            {hasOverride(overridesMap.get(previewItem.itemid)) && (
              <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1.5 }}>
                <Chip
                  label={t('preview.personalized')}
                  size="small"
                  sx={{ height: 22, fontSize: 11, bgcolor: '#EDE5ED', color: '#A55DA0', fontWeight: 600 }}
                />
                <Button size="small" onClick={() => handleResetOverride(previewItem.itemid)} sx={{ textTransform: 'none', color: '#A55DA0' }}>
                  {t('preview.reset_personalization')}
                </Button>
              </Stack>
            )}

            {/* Photo */}
            {!photoHidden && (
              <Box
                component="img"
                src={productPhotoUrl(previewItem.itemcode, photoCacheBust)}
                alt={previewItem.itemcode ?? ''}
                onError={() => setPhotoHidden(true)}
                sx={{
                  width: '100%', maxHeight: 200,
                  objectFit: 'contain', mb: 2,
                  borderRadius: 1, bgcolor: '#f5f5f5',
                }}
              />
            )}

            {/* Marketing title */}
            {previewItem.item_marketingproducttitle && (
              <Typography variant="subtitle2" sx={{ color: '#A55DA0', mb: 0.5 }}>
                {localeField(previewItem, 'item_marketingproducttitle', i18n.language) ?? previewItem.item_marketingproducttitle}
              </Typography>
            )}

            {/* Marketing description (editable) */}
            {editingMarketingDesc && editingMarketingDesc.itemid === previewItem.itemid ? (
              <Stack spacing={0.5} sx={{ mb: 1.5 }}>
                <TextField
                  size="small"
                  fullWidth
                  multiline
                  minRows={2}
                  label="Marketing Description"
                  value={editingMarketingDesc.value}
                  onChange={e => setEditingMarketingDesc({ ...editingMarketingDesc, value: e.target.value })}
                  disabled={savingMarketingDesc}
                />
                <Stack direction="row" spacing={0.5}>
                  <Button size="small" variant="contained" onClick={handleSaveMarketingDesc} disabled={savingMarketingDesc}
                    sx={{ bgcolor: '#A55DA0', '&:hover': { bgcolor: '#8a4d87' } }}>
                    {savingMarketingDesc ? t('preview.saving') : t('preview.save')}
                  </Button>
                  <Button size="small" onClick={() => setEditingMarketingDesc(null)} disabled={savingMarketingDesc}>
                    {t('preview.cancel')}
                  </Button>
                </Stack>
              </Stack>
            ) : (
              <Stack direction="row" alignItems="flex-start" gap={0.5} sx={{ mb: 1.5 }}>
                <Typography variant="body2" sx={{ flex: 1, color: '#555', fontStyle: 'italic' }}>
                  {(localeField(previewItem, 'item_marketingdescription', i18n.language) ?? previewItem.item_marketingdescription) || <em style={{ color: '#bbb' }}>{t('preview.no_marketing_desc')}</em>}
                </Typography>
                {isOnline && (
                  <IconButton size="small" onClick={() => setEditingMarketingDesc({ itemid: previewItem.itemid, value: previewItem.item_marketingdescription ?? '' })} title="Edit marketing description">
                    <EditIcon fontSize="small" sx={{ color: '#A55DA0' }} />
                  </IconButton>
                )}
              </Stack>
            )}

            <Divider sx={{ my: 1 }} />

            {/* Tier (Airline Mode) */}
            {airlineMode && (
              <FormControl size="small" fullWidth sx={{ mb: 1.5 }}>
                <InputLabel>Tier</InputLabel>
                <Select
                  value={tierMap.get(previewItem.itemid) ?? '_none'}
                  label="Tier"
                  onChange={e => {
                    const v = e.target.value as AirlineTier | '_none';
                    if (v === '_none') {
                      db.itemTiers.delete(previewItem.itemid).then(() => {
                        setTierMap(prev => {
                          const next = new Map(prev);
                          next.delete(previewItem.itemid);
                          return next;
                        });
                      });
                    } else {
                      db.itemTiers.put({ itemid: previewItem.itemid, tier: v }).then(() => {
                        setTierMap(prev => new Map(prev).set(previewItem.itemid, v));
                      });
                    }
                  }}
                >
                  <MenuItem value="_none"><em>—</em></MenuItem>
                  {AIRLINE_TIERS.map(t => (
                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Specs */}
            <Stack spacing={0.5} sx={{ mb: 1.5 }}>
              {previewItem.pack_size != null && (
                <Typography variant="body2">
                  {t('preview.case_size', { n: previewItem.pack_size })}
                </Typography>
              )}
              {(() => {
                const packSize = previewItem.pack_size != null && previewItem.pack_size > 0 ? previewItem.pack_size : null;
                const totalWeight = previewItem.final_weight != null && previewItem.final_weight > 0 ? previewItem.final_weight : null;
                const displayWeight =
                  totalWeight != null && packSize != null
                    ? totalWeight / packSize
                    : totalWeight;
                return displayWeight != null ? (
                  <Typography variant="body2">
                    {packSize != null
                      ? t('preview.weight_per_unit', { n: displayWeight.toFixed(0) })
                      : t('preview.weight', { n: displayWeight.toFixed(0) })}
                  </Typography>
                ) : null;
              })()}
              {/* Category6 — FP Category (read-only) */}
              {previewItem.category6_description && (
                <Typography variant="body2">
                  <strong>{t('preview.fp_category')}</strong> {previewItem.category6_description}
                </Typography>
              )}
              {/* Category5 — Website Category: master data (edited in Trac-IT), read-only */}
              <Stack direction="row" alignItems="center" gap={1}>
                <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{t('preview.web_category')}</Typography>
                <Typography variant="body2">{previewItem.category5_description ?? '—'}</Typography>
              </Stack>
              {showPrices && previewItem.item_webprice != null && (
                <Typography variant="body2" sx={{ color: '#A55DA0', fontWeight: 600 }}>
                  {t('preview.price', { n: Number(previewItem.item_webprice).toFixed(2) })}
                </Typography>
              )}
              {showDefrostLife && previewItem.int_life_2 != null && previewItem.int_life_2 > 0 && (
                <Typography variant="body2">
                  {t('preview.defrost_life', { n: Math.round(previewItem.int_life_2) })}
                </Typography>
              )}
              {showHandling && (() => {
                const h = cleanHandlingGuideline(localeField(previewItem, 'item_handling_guideline', i18n.language) ?? previewItem.item_handling_guideline);
                return h ? (
                  <Typography variant="body2">
                    <strong>{t('preview.handling')}</strong> {h}
                  </Typography>
                ) : null;
              })()}
            </Stack>

            {/* Suitability */}
            {getSuitability(previewItem).length > 0 && (
              <>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#555', display: 'block', mb: 0.5 }}>
                  {t('preview.suitable_for')}
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mb: 1.5 }}>
                  {getSuitability(previewItem).map(s => (
                    <Chip key={s} label={t(`suitability.${s}`)} size="small" variant="outlined"
                      sx={{ borderColor: '#4CAF50', color: '#2e7d32', fontWeight: 600 }} />
                  ))}
                </Stack>
              </>
            )}

            {/* Allergens */}
            {getAllergens(previewItem).length > 0 && (
              <>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#555', display: 'block', mb: 0.5 }}>
                  {t('preview.allergens')}
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mb: 1.5 }}>
                  {getAllergens(previewItem).map(a => (
                    <Chip key={a} label={t(`allergen.${a}`)} size="small"
                      sx={{ bgcolor: '#A55DA0', color: '#fff', fontWeight: 600 }} />
                  ))}
                </Stack>
              </>
            )}

            {/* Ingredients / Allergen declaration (toggleable) */}
            {showIngredients && (() => {
              const ingredientText = (localeField(previewItem, 'item_ingredientlist', i18n.language) ?? previewItem.item_ingredientlist)?.trim();
              // Auto-generate allergen declaration from item_is_* flags when no manual ingredient list
              const allergenParts: string[] = [];
              if (previewItem.item_is_gluten)   allergenParts.push('GLUTEN');
              if (previewItem.item_is_dairy)    allergenParts.push('MILK');
              if (previewItem.item_is_egg)      allergenParts.push('EGG');
              if (previewItem.item_is_nuts)     allergenParts.push('NUTS');
              if (previewItem.item_is_soya)     allergenParts.push('SOYA');
              if (previewItem.item_is_fish)     allergenParts.push('FISH');
              if (previewItem.item_is_celery)   allergenParts.push('CELERY');
              if (previewItem.item_is_mustard)  allergenParts.push('MUSTARD');
              if (previewItem.item_is_sesame)   allergenParts.push('SESAME');
              if (previewItem.item_is_sulphites) allergenParts.push('SULPHITES');
              if (previewItem.item_is_lupin)    allergenParts.push('LUPIN');
              if (previewItem.item_is_mollusc)  allergenParts.push('MOLLUSCS');
              const declaration = allergenParts.length > 0 ? `Contains: ${allergenParts.join(', ')}` : null;
              if (!ingredientText && !declaration) return null;
              return (
                <>
                  {ingredientText && (
                    <>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#555', display: 'block', mb: 0.5 }}>
                        {t('preview.ingredients')}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: '#333', lineHeight: 1.5, '& strong': { fontWeight: 700 }, mb: declaration ? 1 : 0 }}
                        dangerouslySetInnerHTML={{ __html: boldAllergens(ingredientText) }}
                      />
                    </>
                  )}
                  {declaration && (
                    <>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#555', display: 'block', mb: 0.5 }}>
                        {t('preview.allergen_decl')}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#333', lineHeight: 1.5 }}>
                        {declaration}
                      </Typography>
                    </>
                  )}
                </>
              );
            })()}
          </Box>
        </Paper>
      )}
      {/* ── PDF Confirmation Dialog ── */}
      <Dialog open={pdfConfirmOpen} onClose={() => setPdfConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1, fontWeight: 700 }}>
          {t('pdfconfirm.title')}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {selectedCount > 0
              ? <>You are about to generate a PDF with <strong>{selectedCount} product{selectedCount === 1 ? '' : 's'}</strong> in the current basket order.</>
              : <>No products selected — the PDF will include all <strong>{filteredItems.length}</strong> visible product{filteredItems.length === 1 ? '' : 's'}.</>
            }
          </Typography>

          {/* Preview list */}
          {(selectedCount > 0 ? selectedItems : filteredItems).length > 0 && (
            <Box sx={{ bgcolor: '#f9f9f9', border: '1px solid #eee', borderRadius: 1, px: 1.5, py: 1, mb: 1.5, maxHeight: 220, overflowY: 'auto' }}>
              {(selectedCount > 0 ? selectedItems : filteredItems).slice(0, 8).map(item => (
                <Typography key={item.itemid} variant="body2" sx={{ fontSize: '0.8rem', py: 0.2, lineHeight: 1.4 }}>
                  <strong>{item.itemcode}</strong> — {item.itemdescription}
                </Typography>
              ))}
              {(selectedCount > 0 ? selectedItems : filteredItems).length > 8 && (
                <Typography variant="caption" sx={{ color: '#888', display: 'block', mt: 0.5 }}>
                  {t('pdfconfirm.and_more', { n: (selectedCount > 0 ? selectedItems : filteredItems).length - 8 })}
                </Typography>
              )}
            </Box>
          )}

          {/* Active settings summary */}
          <Stack direction="row" spacing={0.75} flexWrap="wrap">
            <Typography variant="caption" sx={{ color: '#555', mr: 0.5, alignSelf: 'center' }}>{t('pdfconfirm.settings')}</Typography>
            {airlineMode && <Chip label={t('pdfconfirm.chip_airline')} size="small" sx={{ height: 20, fontSize: 11, bgcolor: '#1976d2', color: '#fff' }} />}
            {showPrices && <Chip label={t('pdfconfirm.chip_prices')} size="small" sx={{ height: 20, fontSize: 11, bgcolor: '#388e3c', color: '#fff' }} />}
            {showIngredients && <Chip label={t('pdfconfirm.chip_ingredients')} size="small" sx={{ height: 20, fontSize: 11, bgcolor: '#f57c00', color: '#fff' }} />}
            {showDefrostLife && <Chip label={t('pdfconfirm.chip_defrost')} size="small" sx={{ height: 20, fontSize: 11, bgcolor: '#0288d1', color: '#fff' }} />}
            {showHandling && <Chip label={t('pdfconfirm.chip_handling')} size="small" sx={{ height: 20, fontSize: 11, bgcolor: '#7b1fa2', color: '#fff' }} />}
            {!airlineMode && !showPrices && !showIngredients && !showDefrostLife && !showHandling && (
              <Typography variant="caption" color="text.secondary">{t('pdfconfirm.standard')}</Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button onClick={() => setPdfConfirmOpen(false)} size="small">
            {t('pdfconfirm.cancel')}
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<PrintIcon />}
            onClick={() => { setPdfConfirmOpen(false); handleGeneratePdf(); }}
            sx={{ bgcolor: '#A55DA0', '&:hover': { bgcolor: '#8d4a88' } }}
          >
            {t('pdfconfirm.generate')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CataloguePage;
